// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Pressable, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { DeckViewer } from '@/features/documentation/DeckViewer';
import { DECKS, isDeckSlug } from '@/features/documentation/decks';

/**
 * One presentation, rendered inside the app. Gated exactly like the list that links here: the
 * deck is a string in the bundle, and this screen is the only place it is turned into a page.
 */
export default function DocumentationDeckScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const { user } = useUser();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const { deck } = useLocalSearchParams<{ deck: string }>();

  if (!canView) {
    return (
      <>
        <Stack.Screen options={{ title: t('documentationPage.title') }} />
        <View style={styles.page}>
          <Text style={styles.paragraph} testID="documentation-deck-forbidden">
            {t('documentationPage.forbidden')}
          </Text>
        </View>
      </>
    );
  }

  if (!isDeckSlug(deck)) {
    return (
      <>
        <Stack.Screen options={{ title: t('documentationPage.title') }} />
        <View style={styles.page}>
          <Text style={styles.paragraph} testID="documentation-deck-unknown">
            {t('documentationPage.unknownDeck')}
          </Text>
          <Pressable style={styles.button} accessibilityRole="button" onPress={() => router.replace('/documentation')}>
            <Text style={styles.buttonText}>{t('documentationPage.back')}</Text>
          </Pressable>
        </View>
      </>
    );
  }

  const { i18nKey, html } = DECKS[deck];
  const title = t(`documentationPage.docs.${i18nKey}.title`);

  return (
    <>
      <Stack.Screen options={{ title }} />
      <View style={styles.viewer}>
        <DeckViewer html={html} title={title} />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1, padding: 16, gap: 12 },
  viewer: { flex: 1 },
  paragraph: { color: appColors.text.secondary },
  button: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  buttonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
});
